import { useState, type ReactNode } from 'react';
import { ArrowRight, CheckCircle2, Info, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { ImportFilesDialog } from '@/components/finance/bank-ingestion';
import { AddTransactionMenu } from '@/components/finance/entry';
import { useSortInboxQueue } from '@/hooks/queries';
import { useFinanceHealth } from '@/hooks/queries/useFinanceDeadlines';
import { useCurrentPeriodReport } from '@/hooks/queries/useCurrentPeriodReport';
import { useCreateAttestation } from '@/hooks/queries/useCommitteeFormation';
import { usePortalUploadModel } from '@/hooks/finance/usePortalUploadModel';
import { useOwedReportWindow } from '@/hooks/finance/useOwedReportWindow';
import { getCampaignTodayString } from '@/lib/calendarUtils';
import { monthYearLabel } from '@/lib/finance/periodLabels';
import { TriageModal } from './TriageModal';
import { reportedOnFileNote } from './reportedOnFileNote';

/**
 * The progress strip above the close ledger (treasurer redesign 2026-06-27).
 *
 * Two halves on one row:
 * - LEFT: a four-segment progress bar + legend (to review / ready / reported /
 *   not reported), driven by the server-computed `health.owedReportReadiness`
 *   counts (ADR-026 PR-A) — the same window the dashboard + masthead read, so the
 *   three surfaces can never disagree, and the readiness stays correct once the
 *   display ledger paginates (the pips need Done/Reported rows the NeedsAction
 *   work feed doesn't carry).
 * - RIGHT: the import → add → triage action cluster. Triage is the primary
 *   action while there is in-period work; it collapses to an "Inbox clear"
 *   status when there is none. Its NeedsAction counts ride the bounded work feed
 *   (`useSortInboxQueue`), never the paginated display query.
 *
 * The progress legend was promoted here from the footer's pip cluster so the
 * treasurer reads "what's left" at the top of the workbench. The footer keeps
 * only the File button. Every testid + state below is unchanged from the
 * pre-redesign strip so the close gate + triage entry behave identically.
 */

// The NeedsAction counter is labelled "to review" (not "not ready"): "not ready"
// and "not reported" collided verbally (both negatives, near-rhyme), so a
// treasurer couldn't tell "0 not ready" from "33 not reported" (issue #596).
// "to review" matches the strip's own review vocabulary (the Triage CTA + the
// "N to review for {month}" stranded line); the report off-ramp keeps its
// canonical "not reported" row term (ResolvedAsCopy, ADR-014/ADR-016). Labels
// only — the `key`s are the unchanged server wire contract.
const SEGMENTS = [
  { key: 'notReady' as const, label: 'to review', bar: 'bg-amber-400', dot: 'bg-amber-400' },
  { key: 'ready' as const, label: 'ready', bar: 'bg-emerald-500', dot: 'bg-emerald-500' },
  { key: 'reported' as const, label: 'reported', bar: 'bg-emerald-800', dot: 'bg-emerald-800' },
  { key: 'notReported' as const, label: 'not reported', bar: 'bg-slate-200', dot: 'bg-slate-300' },
];

// ADR-026 (PR-A) / ADR-017 — the no-open-period (`owedReportReadiness` null)
// case collapses to an all-zero bar rather than a fabricated count: the progress
// track renders empty, never a speculative "22 ready".
const EMPTY_PIPS = { notReady: 0, ready: 0, reported: 0, notReported: 0 } as const;

// §H — a first-timer can't tell how the four counters relate ("5 to review · 22
// ready · 22 reported · 33 not reported" — is it 22 or 55?). The framing that
// they aren't one running total and can overlap now LEADS the numbers as a
// visible caption (was a trailing hover-only "?" icon that read as an
// afterthought), so nobody sums them before reading it (issue #596). The
// per-axis definitions stay on hover. Copy + placement only — the counters
// themselves are unchanged.
const LEGEND_LEAD = 'Not a running total — these track different things and can overlap.';
const LEGEND_HELP =
  "'Ready' means cleared and set to go on a report; 'reported' means on a report you filed.";

export function InboxStrip({ onOpenFile }: { onOpenFile?: () => void } = {}) {
  // The bounded NeedsAction WORK feed (ADR-026 PR-A) — NeedsAction-only,
  // unpaginated. Drives the modal queue, the triage/File gate, and the stranded
  // line, so those work surfaces stay correct after the display ledger paginates.
  const feedRead = useSortInboxQueue(undefined);
  // The readiness pips (all-status: to review / ready / reported / not reported)
  // are computed server-side over the owed window and ride finance-health, NOT
  // the ledger — the NeedsAction feed can't produce Done/Reported/Excluded counts.
  const healthRead = useFinanceHealth();
  const health = healthRead.status === 'ready' ? healthRead.data : null;
  const portal = usePortalUploadModel();
  // Until health answers, which report is owed is unknown: nothing below scopes to a period then,
  // and the calendar period never stands in for the owed one.
  const owedRead = useOwedReportWindow();
  const owedKnown = owedRead.status === 'ready';
  const owed = owedKnown ? owedRead.data : null;
  // The single readiness surface's no-report / late-but-empty states need to
  // know whether a report exists yet for the resolved period. Same period→report
  // resolution the File flow uses, so gate and surface can't drift.
  const currentReport = useCurrentPeriodReport();
  const hasReport = currentReport.status === 'ready' && currentReport.data.hasReport;
  // "I already filed this" (late-but-empty exit) fires the SAME attestation the
  // overdue-dismiss uses — no new endpoint. Destructure the reference-stable mutate.
  const { mutate: attest } = useCreateAttestation();
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);

  if (feedRead.status === 'idle' || feedRead.status === 'loading') {
    return (
      <div data-slot="close-inbox-strip" className="flex flex-col gap-2">
        <Skeleton className="h-10 w-full max-w-sm" />
        <p className="text-sm text-muted-foreground">Loading inbox…</p>
      </div>
    );
  }

  if (feedRead.status !== 'ready') {
    return (
      <div
        data-slot="close-inbox-strip"
        data-testid="close-inbox-strip-error"
        className="rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
      >
        {feedRead.status === 'offline'
          ? "You're offline, so the sort inbox can't load. It will once you reconnect."
          : 'Failed to load the sort inbox. Refresh to retry.'}
      </div>
    );
  }
  const feed = feedRead.data;

  // `items` is now the NeedsAction WORK feed (NeedsAction-only). The envelope
  // scalars ride the same feed response and are identical to the display
  // envelope's (the server filter changes items only). `totalInPeriod` /
  // `totalAllTime` remain NeedsAction-only counts.
  const { items, totalInPeriod, totalAllTime, hasOpenPeriod, periodStart } = feed;
  const olderCount = totalAllTime - totalInPeriod;
  // Readiness pips (all statuses) come from the server-computed
  // owedReportReadiness over the owed window (ADR-026 PR-A). null (no open
  // period) collapses to the empty bar — never a fabricated count (ADR-017).
  // An unanswered health read renders no counts at all (the readiness slot below).
  const pips = health?.owedReportReadiness ?? EMPTY_PIPS;
  const total = pips.notReady + pips.ready + pips.reported + pips.notReported;

  // ── Single readiness surface (redesign) ──
  // The strip's left half is the ONLY readiness surface now. Two special copy
  // states replace the deleted standalone bands:
  //   1. No report exists yet → the bar slot carries "Generate the report to see
  //      filing readiness." (was an orphaned line above the ledger).
  //   2. Late-but-empty → the lead report is PAST DUE and readiness total is 0.
  //      Offers two exits (generate & file / mark already filed) because in-app
  //      Filed doesn't track portal filing.
  // Lead resolution for the late-but-empty exit. useOwedReportWindow is null
  // when the period has no rows at all — but that is exactly the state that
  // needs the two-exit copy (a past-due report with nothing entered may already
  // be filed in the portal, or still needs a nil filing). Fall back to the same
  // next chronological deadline the masthead's pill names, so the strip and the
  // header can never disagree about which report is late.
  const nextDeadline = health?.nextDeadline ?? null;
  const lead =
    owed ??
    (nextDeadline && nextDeadline.periodStart != null && nextDeadline.periodEnd != null
      ? {
          name: nextDeadline.name,
          start: nextDeadline.periodStart,
          end: nextDeadline.periodEnd,
          dueDate: nextDeadline.dueDate,
        }
      : null);
  const leadDueDate = lead?.dueDate ?? null;
  const leadPastDue = leadDueDate != null && leadDueDate < getCampaignTodayString();
  const canAttestLead = lead != null && lead.start != null && lead.end != null;
  const isLateButEmpty = leadPastDue && total === 0 && canAttestLead;
  // "No report yet" shows only when we're NOT already in the late-but-empty state
  // (that state carries its own generate/file copy) and a report genuinely
  // doesn't exist for the resolved period. `hasOpenPeriod` covers the corner the
  // owed window can't: period open, nothing owed yet, no report — since the
  // preflight surface no longer carries this prompt, the strip must, or no
  // surface says it at all.
  const showNoReportPrompt = !isLateButEmpty && !hasReport && (!!owed || hasOpenPeriod);

  const handleAlreadyFiled = () => {
    if (!lead || lead.start == null || lead.end == null) return;
    attest({ scheduleName: lead.name, periodStart: lead.start, periodEnd: lead.end });
  };
  // NeedsAction WORK drives BOTH the triage count and the File gate. The feed is
  // already NeedsAction-only, so the owed branch just window-filters it (no
  // status filter needed). The non-owed branch reads `totalInPeriod` — itself a
  // NeedsAction-only count for the open period, so the owed-window analogue is
  // the same measure over the owed month.
  // Unknown owed report: count all NeedsAction work rather than scope it to the calendar period.
  const scopedNeedsAction = !owedKnown
    ? items.length
    : owed
      ? items.filter((i) => i.date >= owed.start && i.date <= owed.end).length
      : totalInPeriod;
  // Stranded / period-context line. Owed path: period-named "N to review for
  // {Month YYYY} · M older belong to earlier reports" (later-period rows belong
  // to the ledger's collapsed "next period" bucket, not here). Naming the period
  // keeps the count un-mistakable for a filtered view. Fallback: backend counts.
  let strandedLine: ReactNode = null;
  if (owed) {
    // "Older" counts NeedsAction WORK still to triage, not rows rendered (same
    // rationale as the SortInboxService NeedsAction totals): the strip sits above
    // the triage/File actions, so it reports work remaining. Both halves of this
    // line now count NeedsAction work — a Done/Reported June-or-older row inflates
    // neither. Residual window-axis difference (accepted, documented so a later
    // reader doesn't "fix" one branch to match the other): this owed branch counts
    // STRICTLY older (date < owed.start), while the non-owed branch's
    // `olderCount = totalAllTime - totalInPeriod` counts ALL out-of-period
    // NeedsAction (older AND future-dated). The two strips describe different
    // situations and the "belongs to earlier reports" copy renders only here.
    const owedOlderCount = items.filter((i) => i.date < owed.start).length;
    if (owedOlderCount > 0) {
      const inPeriodCount = scopedNeedsAction;
      // Name the full period ("June 2026") so the count can't be mistaken for a
      // filtered view once the ledger gains a Type/Payee filter — the File gate
      // reads this same whole-period NeedsAction number, which the filter must
      // never move (ADR-026 paginated-display context).
      strandedLine = (
        <p data-testid="close-inbox-stranded" className="text-xs text-muted-foreground">
          {inPeriodCount} to review for {owed.periodLabel} ·{' '}
          {owedOlderCount} older {owedOlderCount === 1 ? 'belongs' : 'belong'} to earlier reports
        </p>
      );
    }
  } else if (owedKnown && olderCount > 0) {
    // Name the period ("March 2026") on the in-period arm so the count can't be
    // mistaken for a filtered view or the File-gate number (ADR-026 display
    // context). The older-only arm (no in-period work) has no meaningful period
    // number to name, so it stays unnamed — only the "item/items" noun is dropped.
    strandedLine = (
      <p data-testid="close-inbox-stranded" className="text-xs text-muted-foreground">
        {totalInPeriod > 0
          ? `${totalInPeriod} to review for ${monthYearLabel(periodStart)} · ${olderCount} older ${olderCount === 1 ? 'belongs' : 'belong'} to earlier reports`
          : `${olderCount} older ${olderCount === 1 ? 'belongs' : 'belong'} to earlier reports`}
      </p>
    );
  }

  // §33 — "Reported" means on file (stamped by a filing action, #1007). For portal-upload
  // jurisdictions the ⓘ next to the counts names where it was filed.
  const reportedNote = reportedOnFileNote(portal);

  const importButton = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      data-testid="close-inbox-import"
      onClick={() => setImportOpen(true)}
      className="gap-1.5"
    >
      <Upload className="h-4 w-4" aria-hidden />
      Upload files
    </Button>
  );

  // The primary action (Triage / Ready-to-file / no-period status) is unchanged.
  const primaryAction =
    scopedNeedsAction > 0 ? (
      <Button
        type="button"
        data-testid="close-inbox-triage-cta"
        onClick={() => setOpen(true)}
        className="gap-1.5"
      >
        Triage {scopedNeedsAction} {scopedNeedsAction === 1 ? 'item' : 'items'}
        <ArrowRight className="h-4 w-4" aria-hidden />
      </Button>
    ) : !owedKnown ? null : owed || hasOpenPeriod ? (
      // Triage hit zero with a period open — the strip's primary action flips to
      // filing (the same File flow the card's "Generate report" opens).
      <Button
        type="button"
        data-testid="close-inbox-ready-cta"
        onClick={() => onOpenFile?.()}
        className="gap-1.5"
      >
        <CheckCircle2 className="h-4 w-4" aria-hidden />
        Ready to file
        <ArrowRight className="h-4 w-4" aria-hidden />
      </Button>
    ) : (
      <div
        data-testid="close-inbox-clear"
        className="flex items-center gap-2 text-sm text-muted-foreground"
      >
        <CheckCircle2 className="h-4 w-4 text-emerald-500" />
        <span data-testid="close-inbox-clear-sublabel">No open period</span>
      </div>
    );

  return (
    <div data-slot="close-inbox-strip" className="flex flex-col gap-2.5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {/* The single readiness surface. One labeled bar element: the segmented
            track sits directly above its inline count labels (merged from the old
            standalone bar + dot-legend). Two special copy states replace it. */}
        {healthRead.status === 'failed' || healthRead.status === 'offline' ? (
          <p
            data-testid="close-inbox-readiness-unavailable"
            className="flex-1 text-sm text-muted-foreground"
          >
            {healthRead.status === 'offline'
              ? 'You’re offline. Filing readiness will load when you reconnect.'
              : 'We couldn’t load filing readiness. Refresh to retry.'}
          </p>
        ) : healthRead.status === 'idle' || healthRead.status === 'loading' ? (
          <div data-testid="close-inbox-readiness-loading" aria-hidden className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-2 w-full rounded-full" />
            <Skeleton className="h-4 w-48" />
          </div>
        ) : isLateButEmpty ? (
          // Late-but-empty: the lead report is past due and there is nothing left
          // to prepare. Offer two exits, because in-app Filed doesn't track portal
          // filing (generate & file / mark already filed).
          <div
            data-testid="close-inbox-late-empty"
            className="flex flex-1 flex-wrap items-center gap-3 text-sm text-muted-foreground"
          >
            <p className="flex-1">
              Nothing left to prepare — generate the report and file it in the
              portal, or if you already filed it, mark it filed.
            </p>
            <button
              type="button"
              data-testid="close-inbox-already-filed"
              onClick={handleAlreadyFiled}
              className="shrink-0 rounded border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
            >
              I already filed this
            </button>
          </div>
        ) : showNoReportPrompt ? (
          // No report exists yet: readiness is unknown until one is generated.
          <p
            data-testid="close-inbox-readiness-noreport"
            className="flex-1 text-sm text-muted-foreground"
          >
            Generate the report to see filing readiness.
          </p>
        ) : (
          <div data-testid="close-inbox-readiness-bar" className="flex flex-1 flex-col gap-2">
            {/* Segmented track. Empty when nothing has landed yet. */}
            <div className="flex h-2 w-full overflow-hidden rounded-full bg-slate-100">
              {total > 0 &&
                SEGMENTS.map((s) =>
                  pips[s.key] > 0 ? (
                    <div
                      key={s.key}
                      className={cn('h-full', s.bar)}
                      style={{ width: `${(pips[s.key] / total) * 100}%` }}
                    />
                  ) : null,
                )}
            </div>
            {/* §H — the "these can overlap, not a running total" framing LEADS the
                numbers (issue #596): a visible caption above the counts, so a
                first-timer reads it before trying to sum them. The per-axis
                definitions ('ready' / 'reported') stay on hover. */}
            <p
              data-testid="close-inbox-legend-lead"
              title={`${LEGEND_LEAD} ${LEGEND_HELP}`}
              className="text-xs text-muted-foreground/80"
            >
              {LEGEND_LEAD}
            </p>
            {/* Inline count labels. */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {SEGMENTS.map((s) =>
                s.key === 'notReported' && pips.notReported === 0 ? null : (
                  <span key={s.key} className="inline-flex items-center gap-1.5">
                    <span className={cn('inline-block h-2 w-2 rounded-full', s.dot)} aria-hidden />
                    <span className="tabular-nums text-foreground font-medium">{pips[s.key]}</span>
                    <span>{s.label}</span>
                  </span>
                ),
              )}
              {/* §33 — the "Reported" boundary definition now lives behind this ⓘ
                  affordance next to the counts (portal-upload jurisdictions only),
                  no longer a permanent body paragraph. */}
              {reportedNote && (
                <span
                  data-testid="close-inbox-reported-info"
                  title={reportedNote}
                  className="inline-flex cursor-help items-center text-muted-foreground/70"
                >
                  <Info className="h-3.5 w-3.5" aria-hidden />
                  <span className="sr-only">{reportedNote}</span>
                </span>
              )}
            </div>
          </div>
        )}

        {/* Action cluster (unchanged wiring). */}
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          {importButton}
          <AddTransactionMenu />
          {primaryAction}
        </div>
      </div>

      {/* Older rows (dated before the reporting period) belong to earlier
          reports: surfaced here, never silently dropped, never blended into the
          current-period "ready" count above. Later-period rows (dated after the
          period) live in the ledger's collapsed "next period" bucket, so they
          don't appear in this line. */}
      {strandedLine}

      {/* Mount the modal lazily so its document-level keyboard listener only
          attaches while the modal is actually visible. */}
      {open && <TriageModal items={items} open={open} onClose={() => setOpen(false)} />}

      <ImportFilesDialog open={importOpen} onOpenChange={setImportOpen} />
    </div>
  );
}
